import { useState } from "react";
import { Link } from "react-router-dom";
import { useOrphanedS3Log, type AuditEntry } from "../api/hooks";
import { DataTable, Pagination, createColumnHelper } from "../components/DataTable";
import { Chip, ErrorNotice, Notice, PageHeader } from "../components/ui";
import { auditActionLabel, formatDateTime, shortId } from "../lib/format";

/**
 * Módulo super-admin: claves S3 huérfanas pendientes de limpieza.
 *
 * Una entrada aparece aquí cuando:
 *   - Se borró una firma DeCA o un documento completo de la BD, pero
 *   - El objeto S3 (el PDF firmado o el PDF limpio) no se pudo eliminar
 *     porque tiene un Object Lock activo (COMPLIANCE mode, 366 días).
 *
 * La plataforma registra el evento en AuditLog con `orphanedS3Key` relleno.
 * Cuando el lock expire, el super-admin puede intentar borrar manualmente
 * el objeto desde la consola S3 del proveedor — una vez limpiado, debe
 * marcar la entrada como resuelta (campo `s3Cleaned`).
 *
 * Esta página es de solo lectura: la limpieza física la hace el operador
 * directamente en S3. Aquí se muestra el inventario para no perder el
 * seguimiento.
 */

const column = createColumnHelper<AuditEntry>();

const PAGE_SIZE = 25;

function LockExpiryBadge({ expiry }: { expiry: string | null }) {
  if (!expiry) return <span className="text-ink-400">—</span>;
  const date = new Date(expiry);
  const isPast = date < new Date();
  return (
    <span className={`num text-xs ${isPast ? "text-green-600 dark:text-green-400 font-semibold" : "text-amber-600 dark:text-amber-400"}`}>
      {isPast ? "✅ " : "⏳ "}
      {formatDateTime(expiry)}
    </span>
  );
}

export function OrphanedS3Page() {
  const [page, setPage] = useState(1);
  const query = useOrphanedS3Log({ page, pageSize: PAGE_SIZE });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  const columns = [
    column.accessor("createdAt", {
      header: "Cuándo",
      cell: (info) => (
        <span className="num whitespace-nowrap text-xs">{formatDateTime(info.getValue())}</span>
      ),
    }),
    column.accessor("action", {
      header: "Operación",
      cell: (info) => (
        <span className="text-xs">{auditActionLabel(info.getValue())}</span>
      ),
    }),
    column.display({
      id: "document",
      header: "Documento",
      cell: (info) => {
        const id = info.row.original.decaDocumentId;
        if (!id) return <span className="text-ink-400">—</span>;
        return (
          <Link
            to={`/deca/${id}`}
            className="ident text-xs text-brand-700 hover:underline dark:text-brand-300"
          >
            {shortId(id)}
          </Link>
        );
      },
    }),
    column.accessor("orphanedS3Key", {
      header: "Clave S3 huérfana",
      cell: (info) => {
        const key = info.getValue();
        if (!key) return <span className="text-ink-400">—</span>;
        return (
          <span
            className="ident break-all text-xs text-ink-600 dark:text-ink-300"
            title={key}
          >
            {key.length > 60 ? `…${key.slice(-60)}` : key}
          </span>
        );
      },
    }),
    column.accessor("estimatedLockExpiry", {
      header: "Lock expira",
      cell: (info) => <LockExpiryBadge expiry={info.getValue()} />,
    }),
    column.accessor("s3Cleaned", {
      header: "Limpiado",
      cell: (info) => {
        const cleaned = info.getValue();
        if (cleaned === true) return <Chip tone="ok">Sí</Chip>;
        return <Chip tone="warn">Pendiente</Chip>;
      },
    }),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Plataforma"
        title="Huérfanos S3"
        description='Claves de objetos S3 que no pudieron borrarse al eliminar una firma o documento porque el Object Lock sigue activo. Cuando el lock expire (columna "Lock expira" en verde), el operador puede borrar el objeto desde la consola del proveedor S3.'
      />

      <Notice tone="neutral">
        <span className="font-semibold">¿Qué hacer?</span> Cuando la columna{" "}
        <strong>Lock expira</strong> aparezca en verde, el período de retención ha vencido y el
        objeto ya puede eliminarse manualmente desde la consola S3 del proveedor (AWS, Storadera,
        MinIO…). Esta página no borra nada: es solo el inventario de seguimiento. Una vez limpiado,
        el campo <code>s3Cleaned</code> se actualizará si se registra desde el guardian o de forma
        manual en la BD.
      </Notice>

      <div className="mt-4">
        {query.isError ? (
          <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <>
            {!query.isLoading && total === 0 ? (
              <div className="surface mt-4 py-12 text-center">
                <p className="text-3xl mb-3">✅</p>
                <p className="font-medium text-ink-700 dark:text-ink-200">
                  No hay claves S3 huérfanas pendientes
                </p>
                <p className="mt-1 text-sm text-ink-500 dark:text-ink-400">
                  Todos los borrados de firma y documento se completaron limpiamente en S3.
                </p>
              </div>
            ) : (
              <>
                <p className="mb-3 text-sm text-ink-500 dark:text-ink-400">
                  {total} {total === 1 ? "entrada pendiente" : "entradas pendientes"}
                </p>
                <DataTable
                  columns={columns}
                  data={items}
                  isLoading={query.isLoading}
                  emptyMessage="No hay huérfanos S3 pendientes."
                />
                <Pagination
                  page={query.data?.page ?? page}
                  pageSize={query.data?.pageSize ?? PAGE_SIZE}
                  total={total}
                  onPageChange={setPage}
                />
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
